const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.dev.simscope.click';

export class ApiClient {
  private static getToken(): string | null {
    // Get token from sessionStorage (not localStorage — XSS protection)
    const idToken = sessionStorage.getItem('cognito_id_token');
    return idToken;
  }

  // Access token authorizes Cognito user-facing APIs (e.g. ChangePassword).
  // Different from the ID token the API Gateway authorizer uses.
  static getAccessToken(): string | null {
    return sessionStorage.getItem('cognito_access_token');
  }

  // True when there is no token or the token's `exp` claim is in the past.
  // Used to distinguish a genuine session expiry from an app-level 401
  // returned by a route handler.
  private static isLocalTokenInvalid(token: string | null): boolean {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload.exp !== 'number') return false;
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  private static async request<T>(
    endpoint: string,
    options: RequestInit = {},
    signal?: AbortSignal
  ): Promise<T> {
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal,
    });

    if (!response.ok) {
      if (response.status === 401 && ApiClient.isLocalTokenInvalid(token)) {
        // Genuine session expiry (no token, or token's exp claim is in the past).
        // App-level 401s from route handlers (e.g. wrong current password) leave the
        // local token intact and must NOT force-logout — the caller handles them.
        sessionStorage.removeItem('cognito_id_token');
        sessionStorage.removeItem('cognito_access_token');
        if (
          window.location.pathname !== '/signed-out' &&
          window.location.pathname !== '/callback'
        ) {
          window.location.href = '/';
        }
        throw new Error('Session expired. Redirecting to login...');
      }
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  static async get<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, signal);
  }

  static async post<T>(endpoint: string, data: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body: JSON.stringify(data) }, signal);
  }

  static async put<T>(endpoint: string, data: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) }, signal);
  }

  static async delete<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, signal);
  }
}
